import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, BarChart3, Eye, ListTodo, Play, Timer } from "lucide-react";
import type { ReactNode } from "react";
import { useForm, type Control } from "react-hook-form";
import { Link, useNavigate } from "react-router";
import { z } from "zod";
import { AdSlot } from "@/components/ads/ad-slot";
import { Logo } from "@/components/logo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useCreateRoom } from "@/hooks/use-room";
import { applyServerErrors, type SetFieldError } from "@/lib/form-errors";
import { deckDescription, VOTING_SYSTEMS, type VotingSystem } from "@/lib/poker";

const audience = z.enum(["all", "admin"]);
const schema = z.object({
  name: z.string().trim().min(1, "Give your game a name").max(100, "Keep it under 100 characters"),
  voting_system: z.enum(Object.keys(VOTING_SYSTEMS) as [VotingSystem, ...VotingSystem[]]),
  who_can_reveal: audience,
  who_can_manage_issues: audience,
  auto_reveal: z.boolean(),
  show_average: z.boolean(),
  show_countdown: z.boolean(),
});
type Values = z.infer<typeof schema>;

function AudienceField({
  control,
  name,
  icon,
  label,
}: {
  control: Control<Values>;
  name: "who_can_reveal" | "who_can_manage_issues";
  icon: ReactNode;
  label: string;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="space-y-3">
          <FormLabel className="flex items-center gap-2">
            {icon} {label}
          </FormLabel>
          <FormControl>
            <RadioGroup value={field.value} onValueChange={field.onChange}>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="all" id={`${name}-all`} />
                <Label htmlFor={`${name}-all`} className="text-sm font-normal">
                  All players
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="admin" id={`${name}-admin`} />
                <Label htmlFor={`${name}-admin`} className="text-sm font-normal">
                  Only the game admin
                </Label>
              </div>
            </RadioGroup>
          </FormControl>
        </FormItem>
      )}
    />
  );
}

function ToggleField({
  control,
  name,
  icon,
  label,
  description,
}: {
  control: Control<Values>;
  name: "auto_reveal" | "show_average" | "show_countdown";
  icon: ReactNode;
  label: string;
  description: string;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex items-center justify-between gap-4 space-y-0">
          <div>
            <FormLabel className="flex items-center gap-2">
              {icon} {label}
            </FormLabel>
            <FormDescription className="mt-0.5 text-xs">{description}</FormDescription>
          </div>
          <FormControl>
            <Switch checked={field.value} onCheckedChange={field.onChange} />
          </FormControl>
        </FormItem>
      )}
    />
  );
}

export function CreateRoomPage() {
  const navigate = useNavigate();
  const createRoom = useCreateRoom();
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      voting_system: "fibonacci",
      who_can_reveal: "all",
      who_can_manage_issues: "all",
      auto_reveal: false,
      show_average: true,
      show_countdown: true,
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const room = await createRoom.mutateAsync(values);
      navigate(`/room/${room.id}`);
    } catch (error) {
      const message = applyServerErrors(error, form.setError as SetFieldError);
      if (message) form.setError("root", { message });
    }
  });

  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <Link
          to="/"
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Back to home
        </Link>

        <Card className="border-border/50">
          <CardHeader className="pb-2 text-center">
            <Logo className="mx-auto mb-2 size-10" />
            <CardTitle className="text-2xl font-bold">
              <h1>Create a Room</h1>
            </CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">Set up your estimation session</p>
          </CardHeader>
          <CardContent className="pt-4">
            <Form {...form}>
              <form onSubmit={onSubmit} className="space-y-6" data-testid="create-room-form">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Game name</FormLabel>
                      <FormControl>
                        <Input placeholder="Sprint 42 Planning" autoFocus {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="voting_system"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <BarChart3 className="size-4" /> Voting system
                      </FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {Object.entries(VOTING_SYSTEMS).map(([key, system]) => (
                            <SelectItem key={key} value={key}>
                              <span className="font-medium">{system.label}</span>
                              <span className="ml-2 truncate text-xs text-muted-foreground">
                                {deckDescription(key as VotingSystem)}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />

                <AudienceField
                  control={form.control}
                  name="who_can_reveal"
                  icon={<Eye className="size-4" />}
                  label="Who can reveal cards"
                />
                <AudienceField
                  control={form.control}
                  name="who_can_manage_issues"
                  icon={<ListTodo className="size-4" />}
                  label="Who can manage issues"
                />

                <div className="space-y-4">
                  <ToggleField
                    control={form.control}
                    name="auto_reveal"
                    icon={<Timer className="size-4" />}
                    label="Auto-reveal cards"
                    description="Show cards automatically after everyone voted"
                  />
                  <ToggleField
                    control={form.control}
                    name="show_average"
                    icon={<BarChart3 className="size-4" />}
                    label="Show average in results"
                    description="Include the average value in the voting results"
                  />
                  <ToggleField
                    control={form.control}
                    name="show_countdown"
                    icon={<Play className="size-4" />}
                    label="Show countdown animation"
                    description="A countdown is shown when revealing cards"
                  />
                </div>

                {form.formState.errors.root && (
                  <p role="alert" className="text-sm text-destructive">
                    {form.formState.errors.root.message}
                  </p>
                )}

                <Button
                  type="submit"
                  disabled={createRoom.isPending}
                  className="h-12 w-full text-base font-semibold"
                >
                  {createRoom.isPending ? "Creating..." : "Create Room"}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
        <AdSlot placement="create" className="mt-6" />
      </div>
    </div>
  );
}
